import { useEffect, useMemo } from "react";
import { usePersistedState } from "@/hooks/usePersistedState";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, Package, Bell, Settings as SettingsIcon, X, ArrowRight } from "lucide-react";
import { safeFormatDistance } from "@/lib/dates";
import { PageShell } from "@/components/lembra/PageShell";
import { PageHeader } from "@/components/lembra/PageHeader";
import { ErrorCard } from "@/components/lembra/ErrorCard";
import { PageMeta } from "@/components/lembra/PageMeta";
// Sprint 2.5a /orders-new-redirect, celebration notifications now
// route to /chat with the celebration as initial brief, not the
// legacy bakery cake-builder at /orders/new.
import { celebrationToChatUrl } from "@/lib/celebrationToChat";
import { useOpenAuthedConcierge } from "@/hooks/useOpenAuthedConcierge";
import {
  useAllNotifications,
  type FullNotification,
} from "@/hooks/queries";

export default function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { openFromChatUrl } = useOpenAuthedConcierge();
  const queryClient = useQueryClient();
  // MS69: persist tab across reloads via usePersistedState. Was a
  // local useState that reset to "all" on every visit — a small
  // papercut for power users who live in the unread tab. validate
  // catches stale values from a past enum (e.g. a removed tab) and
  // falls back to "all" without crashing.
  const [tab, setTab] = usePersistedState<string>(
    "lembra:notifications-tab",
    "all",
    {
      validate: (v): v is string =>
        typeof v === "string" &&
        ["all", "unread", "celebrations", "orders"].includes(v),
    },
  );

  const userId = user?.id;
  const notificationsQuery = useAllNotifications(userId);
  // MS138: same useMemo perf pattern as MS136-137. The `?? []` fallback
  // was a fresh array reference each render, causing the 2 downstream
  // useMemo hooks (filter logic at lines 90 and 218) to re-run on every
  // render. Notifications is a heavy list rendering surface.
  const notifications = useMemo(
    () => notificationsQuery.data ?? [],
    [notificationsQuery.data],
  );
  const loading = !user || notificationsQuery.isLoading;

  const notificationsKey = useMemo(
    () => ["notifications", userId, "all"] as const,
    [userId],
  );
  const unreadCountKey = useMemo(
    () => ["notifications", userId, "unread-count"] as const,
    [userId],
  );

  // Optimistic helper: flip is_read on one or all notifications in the cache
  // so the UI doesn't flicker while the DB write is in flight. Per the
  // prompt's red flag on optimistic UI, using setQueryData is the right call
  // here, a naive invalidate would pull fresh data AFTER the DB write and
  // leave the user watching the bold-weight / badge bounce.
  const setReadOptimistic = (predicate: (n: FullNotification) => boolean) => {
    queryClient.setQueryData<FullNotification[]>(notificationsKey, (prev) =>
      (prev ?? []).map((n) => (predicate(n) ? { ...n, is_read: true } : n)),
    );
  };

  const removeFromCache = (id: string) => {
    queryClient.setQueryData<FullNotification[]>(notificationsKey, (prev) =>
      (prev ?? []).filter((n) => n.id !== id),
    );
  };

  const fireReadEvent = () => {
    // Update the sidebar badge immediately + preserve the existing
    // cross-component signal for any other listener.
    queryClient.invalidateQueries({ queryKey: unreadCountKey });
    window.dispatchEvent(new Event("lembra:notifications-read"));
  };

  // Mark all unread as read when the page opens. Matches the prior
  // "visiting /notifications clears the badge" behaviour.
  //
  // Tracked via a hash of (count, id-of-newest-unread) instead of
  // notifications.length so the effect re-fires when a new unread
  // arrives even if total count is unchanged (e.g. one read + one
  // new unread land in the same poll). The previous deps array used
  // notifications.length which silently missed those cases, the
  // page would show new unread items but never clear them server-side.
  const unreadSignature = useMemo(() => {
    const firstUnread = notifications.find((n) => !n.is_read);
    return `${notifications.length}:${firstUnread?.id ?? ""}`;
  }, [notifications]);

  useEffect(() => {
    if (!userId) return;
    const hasUnread = notifications.some((n) => !n.is_read);
    if (!hasUnread) return;

    setReadOptimistic((n) => !n.is_read);
    supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("user_id", userId)
      .eq("is_read", false)
      // MS409: only fire the badge-refresh event if the write
      // succeeded — if it fails the optimistic state is already
      // flipped, the badge will naturally re-appear on next load.
      .then(({ error }) => { if (!error) fireReadEvent(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, unreadSignature]);

  const markAsRead = async (n: FullNotification) => {
    if (!n.is_read) {
      setReadOptimistic((row) => row.id === n.id);
      // MS409: check error so we don't fire the badge-refresh event
      // if the DB write failed (optimistic flip already happened).
      const { error } = await supabase.from("notifications").update({ is_read: true }).eq("id", n.id);
      if (!error) fireReadEvent();
    }

    if (n.related_type === "order" && n.related_id) {
      navigate(`/orders/${n.related_id}`);
      return;
    }

    if (n.related_type === "celebration" && n.related_id) {
      // Approved celebrations need an order, route straight into
      // that flow (or the existing order if one was already placed)
      // so the user can finish what the notification is nudging them
      // to do, instead of bouncing them to the calendar.
      //
      // Sprint 2.5a /orders-new-redirect, for the "needs order"
      // case we now compose a /chat URL with the celebration as
      // initial brief instead of /orders/new. We over-fetch the
      // celebration row's id/type/date/team_member_id + the joined
      // team_member's id/name/start_date so celebrationToChatUrl
      // can build the full brief without a second round-trip.
      const [{ data: cel }, { data: existingOrder }] = await Promise.all([
        supabase
          .from("celebrations")
          .select(
            "id, type, date, status, team_member_id, team_members(id, name, start_date)",
          )
          .eq("id", n.related_id)
          .maybeSingle(),
        supabase.from("orders").select("id").eq("celebration_id", n.related_id).maybeSingle(),
      ]);

      if (cel?.status === "approved") {
        if (existingOrder) {
          navigate(`/orders/${existingOrder.id}`);
          return;
        }
        // Resolve the joined member shape, supabase-js may return
        // the FK row as either an object or a single-element array
        // depending on the FK relationship inference.
        const memberRaw = (cel as { team_members?: unknown }).team_members;
        const member = Array.isArray(memberRaw) ? memberRaw[0] : memberRaw;
        if (
          member &&
          typeof member === "object" &&
          "id" in member &&
          "name" in member
        ) {
          openFromChatUrl(
            celebrationToChatUrl(
              {
                id: cel.id as string,
                type: cel.type as string,
                date: cel.date as string,
              },
              {
                id: (member as { id: string }).id,
                name: (member as { name: string }).name,
                start_date:
                  (member as { start_date?: string | null }).start_date ?? null,
              },
            ),
          );
          return;
        }
        // Defensive fallback when the joined member couldn't be
        // resolved, non-destructive landing on /calendar where
        // the user can re-trigger the flow from the cell.
        navigate("/calendar");
        return;
      }

      navigate("/calendar");
      return;
    }

    if (n.related_type === "celebration") navigate("/calendar");
  };

  const markAllRead = async () => {
    if (!userId) return;
    setReadOptimistic(() => true);
    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("user_id", userId)
      .eq("is_read", false);
    // MS409: only fire badge-refresh when the write succeeded.
    if (!error) fireReadEvent();
  };

  const dismissNotification = async (e: React.MouseEvent, n: FullNotification) => {
    e.stopPropagation();
    removeFromCache(n.id);
    if (!n.is_read) {
      // MS409: guard the badge-refresh on write success.
      // MS438: if the write fails, restore the item to the cache so it
      // doesn't silently vanish and then reappear on the next page load.
      const { error } = await supabase.from("notifications").update({ is_read: true }).eq("id", n.id);
      if (!error) {
        fireReadEvent();
      } else {
        // Rollback: re-insert the notification into the cache so it
        // doesn't vanish from the UI and then reappear on the next load.
        queryClient.setQueryData<FullNotification[]>(notificationsKey, (prev) => {
          if (!prev || prev.some((x) => x.id === n.id)) return prev;
          return [n, ...prev];
        });
      }
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const filtered = useMemo(() => {
    switch (tab) {
      case "unread": return notifications.filter((n) => !n.is_read);
      case "celebrations": return notifications.filter((n) => n.type === "celebration");
      case "orders": return notifications.filter((n) => n.type === "order" || n.type === "order_update");
      default: return notifications;
    }
  }, [tab, notifications]);

  const icon = (type: string) => {
    // MS65: was hardcoded Cake icon. The pivot from cupcake-only to a
    // multi-category catalogue (hampers, vouchers, flowers, experiences,
    // cakes) means a celebration notification could relate to any
    // category — Sparkles is the category-neutral signal we use across
    // Calendar / Onboarding for the same generic "celebration" beat.
    if (type === "celebration") return <Sparkles className="h-5 w-5 text-coral shrink-0" />;
    if (type === "order" || type === "order_update") return <Package className="h-5 w-5 text-navy shrink-0" />;
    if (type === "system" || type === "info") return <SettingsIcon className="h-5 w-5 text-charcoal/60 shrink-0" />;
    return <Bell className="h-5 w-5 text-sky shrink-0" />;
  };

  // Inline relativeTime helper retired in favour of safeFormatDistance
  //, same behaviour (try/catch around invalid dates) but centralised
  // so we don't grow an inconsistent set of try/catch wrappers across
  // pages. Call sites use safeFormatDistance(d, { addSuffix: true })
  // directly.

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent" />
      </div>
    );
  }

  return (
    // MS253: PageShell breathing room. Narrow variant matches the
    // notifications list shape (single column of cards).
    <PageShell width="narrow">
      {/* MS409: noindex — /notifications is an authed-only internal page. */}
      <PageMeta
        title="Notifications · Lembra"
        description="Your Lembra celebration notifications."
        path="/notifications"
        noindex
      />
      {/* MS-batch3: was a hand-rolled <h1> + "Mark all as read" button in
          a `flex justify-between` row, which squished the title and the
          button together on mobile. Migrated to the shared <PageHeader>
          (title + subtitle + tail slot) so /notifications matches Team /
          Calendar / Billing. The unread count Badge sits inline with the
          title; the tail holds "Mark all as read" and stacks below the
          header on mobile via PageHeader's own responsive layout. */}
      <PageHeader
        title="Notifications"
        emphasis="Notifications"
        subtitle="Reminders and order updates, newest first."
        tail={
          <div className="flex items-center gap-3">
            {unreadCount > 0 && <Badge variant="coral">{unreadCount}</Badge>}
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-sm font-medium text-navy hover:underline"
              >
                Mark all as read
              </button>
            )}
          </div>
        }
      />

      {notificationsQuery.isError && (
        <div className="mb-6">
          <ErrorCard
            title="Couldn't load your notifications"
            onRetry={() => notificationsQuery.refetch()}
          />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white mb-6">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="unread">Unread</TabsTrigger>
          <TabsTrigger value="celebrations">Celebrations</TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
        </TabsList>

        <TabsContent value={tab}>
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <Bell className="mx-auto h-12 w-12 text-charcoal/30 mb-4" />
              <h2
                className="mb-2"
                style={{
                  fontFamily: "Newsreader, Georgia, serif",
                  fontWeight: 500,
                  fontSize: "1.4rem",
                  lineHeight: 1.15,
                  letterSpacing: "-0.01em",
                  color: "#1D3461",
                }}
              >
                You're all{" "}
                <em
                  style={{
                    fontStyle: "italic",
                    fontWeight: 500,
                    color: "#C4564A",
                  }}
                >
                  caught up
                </em>
                .
              </h2>
              {/* MS-batch3: the empty-state body used to read "Reminders
                  will land here..." on every tab, including Unread and
                  Orders where that's misleading (you may have plenty of
                  notifications, just none unread). Make it tab-aware. */}
              <p className="text-charcoal/70">
                {tab === "unread"
                  ? "No unread notifications."
                  : tab === "celebrations"
                    ? "No celebration reminders yet."
                    : tab === "orders"
                      ? "No order updates yet."
                      : "Reminders will land here when something's coming up or an order moves."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((n) => (
                <div
                  key={n.id}
                  className={`group relative w-full text-left rounded-xl bg-white p-4 flex items-start gap-4 transition-all hover:shadow-md cursor-pointer ${
                    !n.is_read ? "border-l-4 border-sky shadow-sm" : "opacity-70"
                  }`}
                  onClick={() => markAsRead(n)}
                >
                  <div className="mt-0.5">{icon(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-navy ${!n.is_read ? "font-bold" : "font-medium"}`}>{n.title}</p>
                    <p className="text-sm text-charcoal/70 mt-0.5">{n.message}</p>
                    {/* MS391: strict relative-time. Pre-MS391 the
                        page mixed "28 days ago" + "about 1 month ago"
                        for rows 4 days apart — date-fns'
                        formatDistanceToNow flips formatters at the
                        30-day threshold, qualifier mismatch reads
                        as a bug. Strict variant picks the largest
                        non-zero unit consistently without "about". */}
                    <p className="text-xs text-charcoal/50 mt-1">{safeFormatDistance(n.created_at, { addSuffix: true, strict: true })}</p>
                    {/* MS391: order/celebration attribution. Pre-MS391
                        order-status notifications said "Your bakery
                        has confirmed the order" with no link to WHICH
                        order — the whole row was clickable + routes
                        to /orders/[id] via markAsRead (line 122), but
                        the row gave no signal that's where the click
                        went. Adding an explicit "View order →" /
                        "Open celebration →" tail makes the linkage
                        legible. Visual only — the click handler is
                        already on the wrapping row. */}
                    {/* MS-batch3: this "View order →" / "Open celebration →"
                        tail is the only cue that the whole row is clickable,
                        but at rest it was text-navy/75 (low contrast) with
                        the only emphasis on group-hover — invisible on touch
                        devices that have no hover state. Promote to coral
                        (the brand link/action colour) so it's legible at
                        rest on every device; the arrow nudges right on hover
                        as the desktop affordance. */}
                    {n.related_id && (n.related_type === "order" || n.related_type === "celebration") && (
                      <p className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-coral">
                        {n.related_type === "order" ? "View order" : "Open celebration"}
                        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                      </p>
                    )}
                  </div>
                  {/* MS273: was `opacity-0 group-hover:opacity-100` which
                      hid the dismiss ✕ entirely on touch devices (no
                      hover state on phones / tablets). On mobile a user
                      could see a notification but had no way to dismiss
                      it short of marking-all-read.
                      Fix: always-visible at low opacity on mobile, keep
                      the desktop hover-reveal so the row stays calm at
                      rest. lg: breakpoint matches the rest of the
                      authed chrome (sidebar nav goes mobile-drawer at
                      the same point). */}
                  <button
                    onClick={(e) => dismissNotification(e, n)}
                    className="absolute top-3 right-3 h-6 w-6 flex items-center justify-center rounded-full opacity-60 hover:bg-charcoal/10 transition-opacity lg:opacity-0 lg:group-hover:opacity-100"
                    aria-label="Dismiss"
                  >
                    <X className="h-3.5 w-3.5 text-charcoal/50" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </PageShell>
  );
}
